/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useSpaceAuthStore } from '@coze-common/auth';
import { SpaceRoleType } from '@coze-arch/idl/developer_api';
import { PlaygroundApi } from '@coze-arch/bot-api';

export function useInitSpaceRole(spaceId: string) {
  const { setIsReady, setRoles, isReady } = useSpaceAuthStore(
    useShallow(store => ({
      setIsReady: store.setIsReady,
      setRoles: store.setRoles,
      isReady: store.isReady[spaceId],
    })),
  );

  useEffect(() => {
    let cancelled = false;
    setIsReady(spaceId, false);

    void PlaygroundApi.GetSpaceListV2({})
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        const space = data?.bot_space_list?.find(item => item.id === spaceId);
        const role =
          (space?.space_role_type as SpaceRoleType | undefined) ??
          (space?.role_type as SpaceRoleType | undefined) ??
          SpaceRoleType.Default;
        setRoles(spaceId, [role]);
      })
      .catch(() => {
        if (!cancelled) {
          setRoles(spaceId, [SpaceRoleType.Default]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsReady(spaceId, true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  return isReady;
}
